import React, { useEffect, useState, useMemo } from 'react';
import { couponDistributionRequestsAPI, formatCurrency, formatDate } from '../../services/api';
import { Loader, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const getNumericValue = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'object') {
    if ('$numberDecimal' in value) {
      const parsed = Number(value.$numberDecimal);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if ('$numberDouble' in value) {
      const parsed = Number(value.$numberDouble);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }
  return 0;
};

const InvestorCouponDistributionRequests = () => {
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await couponDistributionRequestsAPI.getMyRequests({ status: statusFilter });
      const data = response.data?.data || response.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const getUserIdValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || value.id || null;
  };

  const canApproveL2 = (request) => {
    if (request.status !== 'pending_l2') return true;
    const l1Approver = getUserIdValue(request.l1ApprovedBy);
    if (!l1Approver || !currentUserId) return true;
    return l1Approver.toString() !== currentUserId.toString();
  };

  const handleApprove = async (request) => {
    try {
      setApprovingId(request._id);
      if (request.status === 'pending') {
        await couponDistributionRequestsAPI.approveRequestL1(request._id);
      } else if (request.status === 'pending_l2') {
        await couponDistributionRequestsAPI.approveRequestL2(request._id);
      }
      await fetchRequests();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve request.');
    } finally {
      setApprovingId(null);
    }
  };

  const getActionLabel = (request) =>
    request.status === 'pending_l2' ? 'Approve L2' : 'Approve';

  const filteredRequests = useMemo(() => {
    if (!searchTerm) {
      return requests;
    }
    const keyword = searchTerm.toLowerCase();
    return requests.filter((request) => {
      const bondName = request.bondId?.bondName?.toLowerCase() || '';
      const isin = request.bondId?.isin?.toLowerCase() || '';
      const paymentType = (request.paymentType || '').toLowerCase();
      const status = (request.status || '').toLowerCase();
      return (
        bondName.includes(keyword) ||
        isin.includes(keyword) ||
        paymentType.includes(keyword) ||
        status.includes(keyword)
      );
    });
  }, [requests, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto space-y-6 max-w-7xl sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Coupon Payment Requests</h1>
          <p className="mt-1 text-sm text-gray-600">Approve coupon payment requests</p>
        </div>

        <div >
          <div className="grid grid-cols-1 gap-4 mb-6 lg:grid-cols-3">
            <div className="card lg:col-span-2">
              <div className="card-body">
                <div className="relative">
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
                  <input
                    type="text"
                    placeholder="Search bonds by name, ISIN, payment type, or status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 form-input"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center card">
              <div className="w-full card-body">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full form-input"
                >
                  <option value="pending">Pending L1</option>
                  <option value="pending_l2">Pending L2</option>
                  <option value="approved">Approved</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Loading requests...
            </div>
          ) : error ? (
            <div className="py-8 text-sm text-center text-red-600">{error}</div>
          ) : filteredRequests.length === 0 ? (
            <div className="py-12 text-sm text-center text-gray-500">No requests found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Payment Date</th>
                    <th className="table-header-cell">Bond</th>
                    <th className="table-header-cell">Payment Type</th>
                    <th className="table-header-cell">Bond Amount</th>
                    <th className="table-header-cell">Amount (MYR)</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Action</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                {filteredRequests.map((request) => (
                    <tr key={request._id}>
                      <td className="table-cell">{formatDate(request.paymentDate)}</td>
                      <td className="table-cell">
                        <div className="font-medium">{request.bondId?.bondName || 'Unknown Bond'}</div>
                        <div className="text-xs text-gray-500">{request.bondId?.isin || request.bondId?.bondNumber}</div>
                      </td>
                      <td className="table-cell">
                        {request.paymentType || 'Coupon'}
                      </td>
                      <td className="table-cell">
                        {getNumericValue(request.tokenAmount).toLocaleString()}
                      </td>
                      <td className="table-cell">
                        {formatCurrency(getNumericValue(request.amount))}
                      </td>
                      <td className="table-cell">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            request.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : request.status === 'pending_l2'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {request.status === 'approved'
                            ? 'Approved'
                            : request.status === 'pending_l2'
                              ? 'Pending L2'
                              : 'Pending L1'}
                        </span>
                      </td>
                      <td className="table-cell">
                        {request.status === 'approved' ? (
                          <span className="text-xs text-gray-500">Approved</span>
                        ) : (
                          <button
                            onClick={() => handleApprove(request)}
                            disabled={
                              approvingId === request._id ||
                              (request.status === 'pending_l2' && !canApproveL2(request))
                            }
                            className="btn-primary btn-sm"
                            title={getActionLabel(request)}
                          >
                            {approvingId === request._id ? 'Approving...' : getActionLabel(request)}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestorCouponDistributionRequests;

